import * as React from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { States } from '../../assets/usaStates.ts'
import { Provinces } from '../../assets/canadianProvinces.ts'
import { useForm as useFormHandler } from '../../hooks/useForm.ts'
import controlStore from '../../store/controlStore.ts'
import dataStore from '../../store/dataStore.ts'
import { Section } from './components/Section.tsx'
import { LabeledInput } from './widgets/LabeledInput.tsx'
import { PhoneNumber } from './widgets/PhoneNumber.tsx'
import { PopupSelect } from './widgets/PopupSelect.tsx'
import { RadioGroup } from './widgets/RadioGroup.tsx'
import { FormLayout } from './components/FormLayout.tsx'
import { Country, SMSOptIn } from './widgets/options.tsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faKey } from '@fortawesome/free-solid-svg-icons/faKey'
import { faCreditCard } from '@fortawesome/free-solid-svg-icons/faCreditCard'
import { ManagePaymentsPopup } from '../../components/common/ManagePayments/ManagePaymentsPopup.tsx'

export const ProfileForm: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
    const profile = dataStore(s => s.account)
    const timezones = dataStore(s => s.timeZones)
        .filter(t => t.status === 'active')
        .sort((a, b) => a.offset <= b.offset ? 1 : -1)
        .map(t => ({ label: t.timezoneName, value: t._id }))

    const defaultValues = {
        ...profile,
        addr1: profile.homeAddress.line1,
        addr2: profile.homeAddress.line2,
        city: profile.homeAddress.city,
        state: profile.homeAddress.state,
        postalCode: profile.homeAddress.postalCode,
        country: profile.homeAddress.country
    }

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
        control,
        trigger,
        watch,
        setValue,
    } = useForm({
        defaultValues: defaultValues,
        mode: 'onChange',
        reValidateMode: 'onBlur',
    })

    const initialCountryRef = React.useRef(defaultValues.country)
    const selectedCountry = watch('country')
    const isCanada = selectedCountry === 'CA'

    React.useEffect(() => {
        void trigger()
    }, [])

    React.useEffect(() => {
        if (selectedCountry !== initialCountryRef.current) {
            setValue('state', '', { shouldDirty: true })
            setValue('postalCode', '', { shouldDirty: true })
            void trigger(['state', 'postalCode'])
        }
        initialCountryRef.current = selectedCountry
    }, [selectedCountry])

    const navigate = useNavigate()
    const isMobile = controlStore(s => s.isOnMobileDevice)
    const [paymentsOpen, setPaymentsOpen] = React.useState(false)

    const [onSubmit] = useFormHandler()

    const cancel = () => {
        if (onClose) {
            onClose()
            return
        }
        navigate('/' + controlStore.getState().currentPage)
    }
    const processForm = (data: any) => {
        void onSubmit('profile', data).then(() => {
            if (onClose) onClose()
        })
    }

    const header = onClose ? null : (
        <div className={'sticky top-0 z-10 flex flex-col bg-base-100 pt-6 pb-2 gap-2'}>
            <div className={'flex items-center justify-between'}>
                <h1 className={'text-2xl font-bold text-primary m-0'}>Profile</h1>
                <button type="button" className={'btn btn-ghost btn-sm text-primary'} onClick={cancel}>
                    Close
                </button>
            </div>
                        <p className={'text-sm text-gray-500 mt-0 text-left'}>Manage your account details</p>
            <div className={'flex items-center gap-2'}>
                <button
                    type="button"
                    className={'btn btn-secondary btn-sm gap-2'}
                    onClick={() => setPaymentsOpen(true)}
                >
                    <FontAwesomeIcon icon={faCreditCard} />
                    Payments
                </button>
                <button
                    type="button"
                    className={'btn btn-secondary btn-sm gap-2'}
                    onClick={() => controlStore.getState().setChangePassword(true)}
                >
                    <FontAwesomeIcon icon={faKey} />
                    Password
                </button>
            </div>
        </div>
    )
    
    const width = onClose ? 'w-full' : (!isMobile ? 'w-[38rem]' : '')

    return (<>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={handleSubmit(processForm)} className={`h-full ${width}`}>
            <FormLayout trigger={trigger} header={header} top>

                <div className={'flex flex-col gap-2 h-[90%] overflow-y-auto'}>
                    <div className={'flex flex-col item-center gap-4'}>
                        {/*  name  */}
                        <Section>
                            <LabeledInput
                                formField={'email'}
                                title={'Email'}
                                {...register('email' as any, {})}
                                errors={errors}
                                disabled={true}
                            />
                            <LabeledInput
                                formField={'firstName'}
                                title={'First Name'}
                                {...register('firstName' as any, {
                                    required: {
                                        value: true,
                                        message: 'Required'
                                    },
                                })}
                                errors={errors}
                            />
                            <LabeledInput
                                formField={'lastName'}
                                title={'Last Name'}
                                {...register('lastName' as any, {
                                    required: {
                                        value: true,
                                        message: 'Required'
                                    },
                                })}
                                errors={errors}
                            />
                        </Section>

                        {/*  email / cell  */}
                        <Section>
                            <PhoneNumber
                                formField={'mobile'}
                                title={'Mobile number'}
                                errors={errors}
                                control={control}
                                required
                            />
                            {/*<LabeledInput*/}
                            {/*    formField={'email'}*/}
                            {/*    title={'Email'}*/}
                            {/*    {...register('email' as any, {})}*/}
                            {/*    errors={errors}*/}
                            {/*    {...{readOnly: true, disabled: true}}*/}
                            {/*/>*/}
                            <PopupSelect
                                control={control}
                                formField={'timeZone'}
                                title={'Timezone'}
                                errors={errors}
                                options={timezones}
                                required
                                showValueLabel
                                btnSize={'lg'}
                                watch={watch}
                                hideComplete
                            />
                            <RadioGroup
                                formField={'smsOptIn'}
                                control={control}
                                options={SMSOptIn}
                                title={'Receive Text Alerts'}
                                errors={errors}
                                required
                            />
                        </Section>

                        {/*  address  */}
                        <Section>
                            <LabeledInput
                                formField={'addr1'}
                                title={'Address'}
                                {...register('addr1' as any, {
                                    required: {
                                        value: true,
                                        message: 'Required'
                                    },
                                })}
                                errors={errors}
                            />
                            <LabeledInput
                                formField={'addr2'}
                                title={'Apt'}
                                {...register('addr2' as any, {})}
                                errors={errors}
                            />
                            <LabeledInput
                                formField={'city'}
                                title={'City'}
                                {...register('city' as any, {
                                    required: {
                                        value: true,
                                        message: 'Required'
                                    },
                                })}
                                errors={errors}
                            />
                            <PopupSelect
                                control={control}
                                formField={'state'}
                                title={isCanada ? 'Province' : 'State'}
                                errors={errors}
                                options={isCanada ? Provinces : States}
                                required
                                showValueLabel
                                btnSize={'sm'}
                                watch={watch}
                                hideComplete
                            />
                            <LabeledInput
                                formField={'postalCode'}
                                title={isCanada ? 'Postal Code' : 'Zip'}
                                {...register('postalCode' as any, {
                                    required: {
                                        value: true,
                                        message: 'Required'
                                    },
                                })}
                                errors={errors}
                            />
                            <RadioGroup
                                formField={'country'}
                                control={control}
                                options={Country}
                                title={'Country'}
                                errors={errors}
                                required/>
                        </Section>
                    </div>

                    <div className={'flex items-center justify-center gap-4'}>
                        {onClose && (
                            <button type="button" className={'btn btn-ghost text-primary'} onClick={cancel}>
                                Cancel
                            </button>
                        )}
                        <input
                            type={'submit'}
                            className={'btn btn-primary'}
                            value={'Update'}
                            disabled={!isDirty || Object.keys(errors).length > 0}
                        />
                    </div>

                </div>

            </FormLayout>
        </form>
        <ManagePaymentsPopup open={paymentsOpen} setOpen={setPaymentsOpen} />
    </>
    )
}
